// ============================================
// PAGE - Fiados
// ============================================
// Gestión de fiados pendientes e histórico

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdSearch,
  MdRefresh,
  MdPerson,
  MdPhone,
  MdAttachMoney,
  MdCalendarToday,
  MdReceipt,
  MdCheckCircle,
  MdCancel,
  MdPayment,
  MdDelete,
  MdRemoveRedEye,
} from 'react-icons/md';
import { useFiadosPendientes, useFiadosHistorico, useEliminarFiado, useUIStore, usePagarFiados } from '@almacenes/core';
import type { FiltrosFiadosPendientes, FiltrosFiadosHistorico, FiadoHistorico } from '@almacenes/core';
import { capitalize, formatPhoneNumber, useShepherdTour, TourFab, type TourStepConfig } from '@almacenes/ui';
import { useRutFormatter } from '../../hooks/useRutFormatter';
import { useTourPersistido } from '@/hooks/useTourPersistido';
import logo from '@/assets/logos/nuevo.svg';
import { ModalPagarFiado } from './components/ModalPagarFiado';
import { ModalDetalleFiado } from './components/ModalDetalleFiado';

type TabType = 'pendientes' | 'historico';

const TOUR_ID = 'fiados-home';
const TOUR_VERSION = 'v1';

const buildSteps = (setActiveTab: (tab: TabType) => void): TourStepConfig[] => [
  {
    id: 'intro',
    title: 'Fiados',
    text: 'Acá llevas el control de lo que te deben tus clientes: lo que está pendiente de cobro y todo lo que ya se pagó.',
    attachTo: { element: '[data-tour="fiados-header"]', on: 'bottom' },
  },
  {
    id: 'tabs',
    title: 'Dos pestañas',
    text: '«Fiados Pendientes» te muestra a quién le tienes que cobrar, agrupado por cliente. «Histórico de Fiados» te muestra boleta por boleta, incluidas las que ya están pagadas.',
    attachTo: { element: '[data-tour="fiados-tabs"]', on: 'bottom' },
  },
  {
    id: 'filtros-pendientes',
    title: 'Buscar un cliente',
    text: 'Filtra por nombre (mínimo 3 letras) o por RUT y aprieta Buscar. «Limpiar Filtros» vuelve a mostrarte la lista completa.',
    attachTo: { element: '[data-tour="fiados-filtros-pendientes"]', on: 'bottom' },
    beforeShow: () => setActiveTab('pendientes'),
    waitForSelector: true,
  },
  {
    id: 'tabla-pendientes',
    title: 'La lista de deudores',
    text: 'Cada fila es un cliente con el total que te debe. Haz clic en la fila para ver sus fiados uno por uno y registrarle los pagos.',
    attachTo: { element: '[data-tour="fiados-tabla-pendientes"]', on: 'bottom' },
    beforeShow: () => setActiveTab('pendientes'),
    waitForSelector: true,
  },
  {
    id: 'historico',
    title: 'Histórico de Fiados',
    text: 'Acá buscas fiados boleta por boleta dentro de un rango de fechas. Las fechas Desde y Hasta son obligatorias, y puedes dejar solo los pagados o solo los pendientes.',
    attachTo: { element: '[data-tour="fiados-filtros-historico"]', on: 'bottom' },
    beforeShow: () => setActiveTab('historico'),
    waitForSelector: true,
  },
  {
    id: 'acciones-historico',
    title: 'Acciones de cada fiado',
    text: 'En cada fila: el ojo abre el detalle de la boleta, el ícono de pago registra un abono y el basurero elimina el fiado. Los dos últimos solo aparecen si el fiado sigue pendiente.',
    attachTo: { element: '[data-tour="fiados-historico-acciones"]', on: 'bottom' },
    beforeShow: () => setActiveTab('historico'),
    waitForSelector: true,
  },
];

export const FiadosPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('pendientes');
  const addToast = useUIStore((state) => state.addToast);

  // Filtros Pendientes
  const [filtrosPendientes, setFiltrosPendientes] = useState<FiltrosFiadosPendientes>({});
  const [inputNombrePendientes, setInputNombrePendientes] = useState('');
  const { rawValue: inputRutPendientes, formattedValue: formattedRutPendientes, handleChange: handleRutChange, reset: resetRut } = useRutFormatter('');
  const [paginaPendientes, setPaginaPendientes] = useState(0);

  // Filtros Histórico
  const [filtrosHistorico, setFiltrosHistorico] = useState<FiltrosFiadosHistorico>({
    fechaDesde: '',
    fechaHasta: '',
  });
  const [inputNombreHistorico, setInputNombreHistorico] = useState('');
  const [inputPagado, setInputPagado] = useState('');
  const [inputFechaDesde, setInputFechaDesde] = useState('');
  const [inputFechaHasta, setInputFechaHasta] = useState('');
  const [paginaHistorico, setPaginaHistorico] = useState(0);

  // Estado para modal de confirmación de eliminación
  const [modalEliminar, setModalEliminar] = useState<{ open: boolean; fiadoId: number | null }>({
    open: false,
    fiadoId: null,
  });

  // Estado para modal de pago
  const [modalPagar, setModalPagar] = useState<{ open: boolean; fiado: FiadoHistorico | null }>({
    open: false,
    fiado: null,
  });

  // Estado para modal de detalle
  const [modalDetalle, setModalDetalle] = useState<{ open: boolean; fiado: FiadoHistorico | null }>({
    open: false,
    fiado: null,
  });

  // Queries
  const { data: dataPendientes, isLoading: isLoadingPendientes, error: errorPendientes, refetch: refetchPendientes } = 
    useFiadosPendientes(activeTab === 'pendientes' ? { ...filtrosPendientes, page: paginaPendientes } : undefined);
  
  const { data: dataHistorico, isLoading: isLoadingHistorico, error: errorHistorico, refetch: refetchHistorico } = 
    useFiadosHistorico({ ...filtrosHistorico, page: paginaHistorico }, activeTab === 'historico' && !!inputFechaDesde && !!inputFechaHasta);

  // Mutation - usa los mismos filtros que la query para que el optimistic update funcione
  const { mutate: eliminarFiado, isPending: isEliminando } = useEliminarFiado({ ...filtrosHistorico, page: paginaHistorico });
  const { mutate: pagarFiados, isPending: isPagando } = usePagarFiados();

  // Resetear página al cambiar de pestaña
  useEffect(() => {
    if (activeTab === 'pendientes') {
      setPaginaPendientes(0);
    } else {
      setPaginaHistorico(0);
    }
  }, [activeTab]);

  // Handlers Pendientes
  const handleBuscarPendientes = () => {
    setPaginaPendientes(0); // Resetear a primera página
    setFiltrosPendientes({
      nombreCliente: inputNombrePendientes.length >= 3 ? inputNombrePendientes : undefined,
      rutDv: inputRutPendientes.length >= 7 ? formattedRutPendientes : undefined,
    });
  };

  const limpiarFiltrosPendientes = () => {
    setPaginaPendientes(0);
    setFiltrosPendientes({});
    setInputNombrePendientes('');
    resetRut();
  };

  const handlePaginaPendientes = (nuevaPagina: number) => {
    setPaginaPendientes(nuevaPagina);
  };

  // Handlers Histórico
  const handleBuscarHistorico = () => {
    if (!inputFechaDesde || !inputFechaHasta) {
      addToast('error', 'Las fechas Desde y Hasta son obligatorias');
      return;
    }

    setPaginaHistorico(0); // Resetear a primera página
    setFiltrosHistorico({
      nombreCliente: inputNombreHistorico.length >= 3 ? inputNombreHistorico : undefined,
      pagado: inputPagado ? inputPagado === 'true' : undefined,
      fechaDesde: inputFechaDesde,
      fechaHasta: inputFechaHasta,
    });
  };

  const limpiarFiltrosHistorico = () => {
    setPaginaHistorico(0);
    setFiltrosHistorico({
      fechaDesde: '',
      fechaHasta: '',
    });
    setInputNombreHistorico('');
    setInputPagado('');
    setInputFechaDesde('');
    setInputFechaHasta('');
  };

  const handlePaginaHistorico = (nuevaPagina: number) => {
    setPaginaHistorico(nuevaPagina);
  };

  // Handler para abrir modal de confirmación de eliminación
  const handleAbrirModalEliminar = (fiadoId: number) => {
    setModalEliminar({ open: true, fiadoId });
  };

  // Handler para cerrar modal
  const handleCerrarModalEliminar = () => {
    setModalEliminar({ open: false, fiadoId: null });
  };

  // Handler para confirmar eliminación
  const handleConfirmarEliminar = () => {
    if (modalEliminar.fiadoId) {
      eliminarFiado(modalEliminar.fiadoId, {
        onSuccess: () => {
          handleCerrarModalEliminar();
          addToast('success', 'Fiado eliminado exitosamente');
        },
        onError: (error) => {
          console.error('Error al eliminar fiado:', error);
          addToast('error', 'Error al eliminar el fiado. Por favor, intente nuevamente.');
        },
      });
    }
  };

  // Handler para pagar fiado
  const handlePagar = (fiado: FiadoHistorico) => {
    setModalPagar({ open: true, fiado });
  };

  // Handler para cerrar modal de pago
  const handleCerrarModalPagar = () => {
    setModalPagar({ open: false, fiado: null });
  };

  // Handler para confirmar pago
  const handleConfirmarPago = (tipoPago: 'EFECTIVO' | 'DEBITO' | 'CREDITO' | 'TRANSFERENCIA', monto: number) => {
    if (!modalPagar.fiado) return;

    pagarFiados(
      {
        pagos: [
          {
            idFiado: modalPagar.fiado.fiadoId,
            tipoPago,
            monto,
          },
        ],
      },
      {
        onSuccess: () => {
          handleCerrarModalPagar();
          addToast('success', 'Pago registrado exitosamente');
        },
        onError: (error) => {
          console.error('Error al pagar fiado:', error);
          addToast('error', 'Error al registrar el pago. Por favor, intente nuevamente.');
        },
      }
    );
  };

  // Handler para ver detalle
  const handleVerDetalle = (fiado: FiadoHistorico) => {
    setModalDetalle({ open: true, fiado });
  };

  // Handler para cerrar modal de detalle
  const handleCerrarModalDetalle = () => {
    setModalDetalle({ open: false, fiado: null });
  };

  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const steps = useMemo(() => buildSteps(setActiveTab), []);
  const { start } = useShepherdTour(steps, { logoUrl: logo, name: 'Almacenes Digitales' });
  const { seen, markAsSeen } = useTourPersistido(TOUR_ID, TOUR_VERSION);

  useEffect(() => {
    if (!seen) {
      start();
      markAsSeen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seen]);

  return (
    <div className="px-4 md:px-6 lg:px-8 pb-8">
      <TourFab onClick={start} />
      {/* Header */}
      <div className="mb-6" data-tour="fiados-header">
        <h1 className="text-3xl font-bold text-[#11194F] mb-2">Fiados</h1>
        <p className="text-gray-600">Gestión de créditos a clientes</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200" data-tour="fiados-tabs">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('pendientes')}
            className={`pb-3 px-2 font-medium transition-colors border-b-2 ${
              activeTab === 'pendientes'
                ? 'text-[#43BFFF] border-[#43BFFF]'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Fiados Pendientes
          </button>
          <button
            onClick={() => setActiveTab('historico')}
            className={`pb-3 px-2 font-medium transition-colors border-b-2 ${
              activeTab === 'historico'
                ? 'text-[#43BFFF] border-[#43BFFF]'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Histórico de Fiados
          </button>
        </div>
      </div>

      {/* Contenido de las pestañas */}
      {activeTab === 'pendientes' ? (
        <>
          {/* Filtros Pendientes */}
          <div className="bg-white rounded-xl shadow mb-6 p-6" data-tour="fiados-filtros-pendientes">
            <h2 className="text-lg font-bold text-[#11194F] mb-4">Filtros de Búsqueda</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nombre Cliente */}
              <div>
                <label className="block text-sm font-medium text-[#11194F] mb-2">
                  Nombre Cliente
                  {inputNombrePendientes.length > 0 && inputNombrePendientes.length < 3 && (
                    <span className="text-xs text-orange-500 ml-2">(mín. 3 caracteres)</span>
                  )}
                </label>
                <input
                  type="text"
                  value={inputNombrePendientes}
                  onChange={(e) => setInputNombrePendientes(e.target.value)}
                  placeholder="Buscar por nombre..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleBuscarPendientes();
                    }
                  }}
                />
              </div>

              {/* RUT */}
              <div>
                <label className="block text-sm font-medium text-[#11194F] mb-2">
                  RUT
                </label>
                <input
                  type="text"
                  value={formattedRutPendientes}
                  onChange={handleRutChange}
                  placeholder="12.345.678-9"
                  maxLength={12}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleBuscarPendientes();
                    }
                  }}
                />
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleBuscarPendientes}
                className="px-6 py-2 bg-[#43BFFF] text-white rounded-lg hover:bg-[#2BA5E8] transition-colors flex items-center gap-2"
              >
                <MdSearch className="text-xl" />
                Buscar
              </button>
              <button
                onClick={limpiarFiltrosPendientes}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Limpiar Filtros
              </button>
              <button
                onClick={() => refetchPendientes()}
                disabled={isLoadingPendientes}
                className="flex items-center gap-2 px-4 py-2 border border-[#43BFFF] text-[#43BFFF] rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
              >
                <MdRefresh className={`text-xl ${isLoadingPendientes ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
            </div>
          </div>

          {/* Tabla Pendientes */}
          <div className="bg-white rounded-xl shadow overflow-hidden" data-tour="fiados-tabla-pendientes">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F3F4F9]">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-[#11194F]">Cliente</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-[#11194F]">Teléfono</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-[#11194F]">RUT</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-[#11194F]">Saldo Pendiente</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingPendientes ? (
                    <tr>
                      <td colSpan={4} className="text-center py-12">
                        <div className="flex justify-center">
                          <div className="w-8 h-8 border-4 border-[#43BFFF] border-t-transparent rounded-full animate-spin" />
                        </div>
                        <p className="mt-4 text-gray-600">Cargando fiados...</p>
                      </td>
                    </tr>
                  ) : errorPendientes ? (
                    <tr>
                      <td colSpan={4} className="text-center py-12 text-red-500">
                        Error al cargar los fiados. Por favor, intente nuevamente.
                      </td>
                    </tr>
                  ) : dataPendientes && dataPendientes.content.length > 0 ? (
                    dataPendientes.content.map((cliente, index) => (
                      <tr
                        key={cliente.clienteAlmacenId}
                        onClick={() => cliente.clienteAlmacenId && navigate(`/fiados/cliente/${cliente.clienteAlmacenId}`)}
                        className={`transition-colors ${
                          cliente.clienteAlmacenId ? 'cursor-pointer' : 'cursor-default'
                        } ${index % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-gray-50 hover:bg-blue-50'}`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <MdPerson className="text-gray-400" />
                            <span className="text-sm text-gray-900">{capitalize((cliente.aliasNombre || cliente.clienteNombre) ?? 'Cliente sin registro')}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <MdPhone className="text-gray-400" />
                            <span className="text-sm text-gray-900">{formatPhoneNumber(cliente.clienteTelefono)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {cliente.clienteRut && cliente.clienteDv 
                            ? `${cliente.clienteRut}-${cliente.clienteDv}` 
                            : '-'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <MdAttachMoney className="text-red-500" />
                            <span className="text-sm font-bold text-red-600">
                              ${cliente.saldoPendiente.toLocaleString('es-CL')}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center py-12 text-gray-500">
                        No se encontraron fiados pendientes con los filtros seleccionados
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación Info */}
            {dataPendientes && dataPendientes.content.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <p className="text-sm text-gray-600">
                    Mostrando {dataPendientes.content.length} de {dataPendientes.totalElements} clientes
                  </p>
                  
                  {/* Controles de paginación */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePaginaPendientes(paginaPendientes - 1)}
                      disabled={paginaPendientes === 0}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Anterior
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: dataPendientes.totalPages }, (_, i) => i).map((page) => {
                        // Mostrar solo páginas cercanas a la actual
                        if (
                          page === 0 ||
                          page === dataPendientes.totalPages - 1 ||
                          (page >= paginaPendientes - 1 && page <= paginaPendientes + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => handlePaginaPendientes(page)}
                              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                                page === paginaPendientes
                                  ? 'bg-[#43BFFF] text-white'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {page + 1}
                            </button>
                          );
                        } else if (page === paginaPendientes - 2 || page === paginaPendientes + 2) {
                          return <span key={page} className="px-2 text-gray-400">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    
                    <button
                      onClick={() => handlePaginaPendientes(paginaPendientes + 1)}
                      disabled={paginaPendientes >= dataPendientes.totalPages - 1}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Filtros Histórico */}
          <div className="bg-white rounded-xl shadow mb-6 p-6" data-tour="fiados-filtros-historico">
            <h2 className="text-lg font-bold text-[#11194F] mb-4">Filtros de Búsqueda</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Nombre Cliente */}
              <div>
                <label className="block text-sm font-medium text-[#11194F] mb-2">
                  Nombre Cliente
                  {inputNombreHistorico.length > 0 && inputNombreHistorico.length < 3 && (
                    <span className="text-xs text-orange-500 ml-2">(mín. 3 caracteres)</span>
                  )}
                </label>
                <input
                  type="text"
                  value={inputNombreHistorico}
                  onChange={(e) => setInputNombreHistorico(e.target.value)}
                  placeholder="Buscar por nombre..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none"
                />
              </div>

              {/* Estado Pagado */}
              <div>
                <label className="block text-sm font-medium text-[#11194F] mb-2">Estado de Pago</label>
                <select
                  value={inputPagado}
                  onChange={(e) => setInputPagado(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none"
                >
                  <option value="">Todos</option>
                  <option value="true">Pagado</option>
                  <option value="false">Pendiente</option>
                </select>
              </div>

              {/* Fecha Desde */}
              <div>
                <label className="block text-sm font-medium text-[#11194F] mb-2">
                  Fecha Desde <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={inputFechaDesde}
                  onChange={(e) => setInputFechaDesde(e.target.value)}
                  onClick={(e) => e.currentTarget.showPicker?.()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none"
                />
              </div>

              {/* Fecha Hasta */}
              <div>
                <label className="block text-sm font-medium text-[#11194F] mb-2">
                  Fecha Hasta <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={inputFechaHasta}
                  onChange={(e) => setInputFechaHasta(e.target.value)}
                  onClick={(e) => e.currentTarget.showPicker?.()}
                  min={inputFechaDesde || undefined}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none"
                />
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleBuscarHistorico}
                disabled={!inputFechaDesde || !inputFechaHasta}
                className="px-6 py-2 bg-[#43BFFF] text-white rounded-lg hover:bg-[#2BA5E8] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MdSearch className="text-xl" />
                Buscar
              </button>
              <button
                onClick={limpiarFiltrosHistorico}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Limpiar Filtros
              </button>
              <button
                onClick={() => refetchHistorico()}
                disabled={isLoadingHistorico || !inputFechaDesde || !inputFechaHasta}
                className="flex items-center gap-2 px-4 py-2 border border-[#43BFFF] text-[#43BFFF] rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
              >
                <MdRefresh className={`text-xl ${isLoadingHistorico ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
            </div>
          </div>

          {/* Tabla Histórico */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F3F4F9]">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-[#11194F]">N° Boleta</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-[#11194F]">Cliente</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-[#11194F]">Fecha</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-[#11194F]">Estado</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-[#11194F]">Total</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-[#11194F]">Abonado</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-[#11194F]">Pendiente</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-[#11194F]" data-tour="fiados-historico-acciones">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingHistorico ? (
                    <tr>
                      <td colSpan={8} className="text-center py-12">
                        <div className="flex justify-center">
                          <div className="w-8 h-8 border-4 border-[#43BFFF] border-t-transparent rounded-full animate-spin" />
                        </div>
                        <p className="mt-4 text-gray-600">Cargando histórico...</p>
                      </td>
                    </tr>
                  ) : errorHistorico ? (
                    <tr>
                      <td colSpan={8} className="text-center py-12 text-red-500">
                        Error al cargar el histórico. Por favor, intente nuevamente.
                      </td>
                    </tr>
                  ) : dataHistorico && dataHistorico.content.length > 0 ? (
                    dataHistorico.content.map((fiado, index) => (
                      <tr key={fiado.fiadoId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <MdReceipt className="text-gray-400" />
                            <span className="text-sm font-semibold text-[#11194F]">#{fiado.boletaId}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm text-gray-900">{capitalize((fiado.aliasNombre || fiado.clienteNombre) ?? 'Cliente sin registro')}</p>
                            <p className="text-xs text-gray-500">{fiado.clienteTelefono ? formatPhoneNumber(fiado.clienteTelefono) : '-'}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <MdCalendarToday className="text-gray-400 text-sm" />
                            <span className="text-sm text-gray-900">{formatearFecha(fiado.fecha)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {fiado.pagado ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <MdCheckCircle />
                              Pagado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                              <MdCancel />
                              Pendiente
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-gray-900">
                          ${fiado.montoTotal.toLocaleString('es-CL')}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-green-600">
                          ${fiado.montoAbonado.toLocaleString('es-CL')}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-bold text-red-600">
                          ${fiado.saldoPendiente.toLocaleString('es-CL')}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleVerDetalle(fiado)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Ver detalle"
                            >
                              <MdRemoveRedEye className="text-xl" />
                            </button>
                            {!fiado.pagado ? (
                              <>
                                <button
                                  onClick={() => handlePagar(fiado)}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Pagar fiado"
                                >
                                  <MdPayment className="text-xl" />
                                </button>
                                <button
                                  onClick={() => handleAbrirModalEliminar(fiado.fiadoId)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Eliminar fiado"
                                >
                                  <MdDelete className="text-xl" />
                                </button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center py-12 text-gray-500">
                        {!inputFechaDesde || !inputFechaHasta 
                          ? 'Seleccione el rango de fechas para buscar'
                          : 'No se encontraron fiados con los filtros seleccionados'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación Info */}
            {dataHistorico && dataHistorico.content.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <p className="text-sm text-gray-600">
                    Mostrando {dataHistorico.content.length} de {dataHistorico.totalElements} fiados
                  </p>
                  
                  {/* Controles de paginación */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePaginaHistorico(paginaHistorico - 1)}
                      disabled={paginaHistorico === 0}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Anterior
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: dataHistorico.totalPages }, (_, i) => i).map((page) => {
                        // Mostrar solo páginas cercanas a la actual
                        if (
                          page === 0 ||
                          page === dataHistorico.totalPages - 1 ||
                          (page >= paginaHistorico - 1 && page <= paginaHistorico + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => handlePaginaHistorico(page)}
                              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                                page === paginaHistorico
                                  ? 'bg-[#43BFFF] text-white'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {page + 1}
                            </button>
                          );
                        } else if (page === paginaHistorico - 2 || page === paginaHistorico + 2) {
                          return <span key={page} className="px-2 text-gray-400">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    
                    <button
                      onClick={() => handlePaginaHistorico(paginaHistorico + 1)}
                      disabled={paginaHistorico >= dataHistorico.totalPages - 1}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal de confirmación para eliminar fiado */}
      {modalEliminar.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <MdDelete className="text-2xl text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#11194F]">Eliminar Fiado</h3>
                  <p className="text-sm text-gray-600">Esta acción no se puede deshacer</p>
                </div>
              </div>

              <p className="text-gray-700 mb-6">
                ¿Está seguro que desea eliminar este fiado? Se eliminará permanentemente del sistema.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleCerrarModalEliminar}
                  disabled={isEliminando}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmarEliminar}
                  disabled={isEliminando}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isEliminando ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <MdDelete />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de pago */}
      {modalPagar.open && modalPagar.fiado && (
        <ModalPagarFiado
          fiado={modalPagar.fiado}
          onClose={handleCerrarModalPagar}
          onPagar={handleConfirmarPago}
          isPaying={isPagando}
        />
      )}

      {/* Modal de detalle */}
      {modalDetalle.open && modalDetalle.fiado && (
        <ModalDetalleFiado
          fiado={modalDetalle.fiado}
          onClose={handleCerrarModalDetalle}
        />
      )}
    </div>
  );
};
